import {
    getCart,
    setQty,
    removeItem,
    restoreItem,
    getSubtotal,
    getCount,
} from "./cart.js";

const CHECKOUT_STORAGE_KEY = "jabuilds_checkout_summary";

let drawerEl = null;
let undoTimer = null;

const priceFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

function formatPrice(n) {
    return priceFormat.format(n);
}

function buildSummaryText(cart) {
    const lines = cart.items.map(
        (item) => `${item.qty}x ${item.name} (${item.priceLabel})`,
    );
    lines.push("", `Estimated subtotal: ${formatPrice(getSubtotal())}`);
    return lines.join("\n");
}

function buildItemRow(item) {
    const row = document.createElement("div");
    row.className = "cart-drawer__item";

    const img = document.createElement("img");
    img.src = item.image;
    img.alt = "";
    img.width = 64;
    img.height = 64;
    row.appendChild(img);

    const info = document.createElement("div");
    info.className = "cart-drawer__item-info";

    const name = document.createElement("div");
    name.className = "cart-drawer__item-name";
    name.textContent = item.name;
    info.appendChild(name);

    const price = document.createElement("div");
    price.className = "cart-drawer__item-price";
    price.textContent = item.priceLabel;
    info.appendChild(price);

    const qtyRow = document.createElement("div");
    qtyRow.className = "cart-drawer__qty-row";

    const minus = document.createElement("button");
    minus.type = "button";
    minus.textContent = "−";
    minus.setAttribute("aria-label", `Decrease quantity of ${item.name}`);
    minus.addEventListener("click", () =>
        item.qty === 1 ? removeWithUndo(item) : setQty(item.id, item.qty - 1),
    );

    const qty = document.createElement("span");
    qty.className = "cart-drawer__qty";
    qty.textContent = String(item.qty);
    qty.setAttribute("aria-label", `Quantity ${item.qty}`);

    const plus = document.createElement("button");
    plus.type = "button";
    plus.textContent = "+";
    plus.setAttribute("aria-label", `Increase quantity of ${item.name}`);
    plus.addEventListener("click", () => setQty(item.id, item.qty + 1));

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "cart-drawer__remove";
    remove.textContent = "Remove";
    remove.setAttribute("aria-label", `Remove ${item.name}`);
    remove.addEventListener("click", () => removeWithUndo(item));

    qtyRow.append(minus, qty, plus, remove);
    info.appendChild(qtyRow);
    row.appendChild(info);
    return row;
}

// Removing is immediate but reversible for a few seconds: the footer
// shows "Removed X — Undo" until the timer runs out or the cart changes.
function removeWithUndo(item) {
    removeItem(item.id);
    const status = drawerEl.querySelector(".cart-drawer__undo");
    status.replaceChildren();
    const text = document.createElement("span");
    text.textContent = `Removed ${item.name}.`;
    const undo = document.createElement("button");
    undo.type = "button";
    undo.className = "cart-drawer__undo-btn";
    undo.textContent = "Undo";
    undo.addEventListener("click", () => {
        clearTimeout(undoTimer);
        status.replaceChildren();
        restoreItem(item);
    });
    status.append(text, " ", undo);
    clearTimeout(undoTimer);
    undoTimer = setTimeout(() => status.replaceChildren(), 6000);
}

function render() {
    if (!drawerEl) return;
    const cart = getCart();
    const body = drawerEl.querySelector(".cart-drawer__body");
    const subtotalEl = drawerEl.querySelector(".cart-drawer__subtotal");
    const checkoutBtn = drawerEl.querySelector(".cart-drawer__checkout");

    body.replaceChildren();
    if (cart.items.length === 0) {
        const empty = document.createElement("p");
        empty.className = "cart-drawer__empty";
        empty.textContent =
            "Your cart is empty. Pick a build on the Shop page, or book a free call to spec one together.";
        body.appendChild(empty);
    } else {
        cart.items.forEach((item) => body.appendChild(buildItemRow(item)));
    }

    subtotalEl.textContent = `Estimated subtotal: ${formatPrice(getSubtotal())}`;
    checkoutBtn.disabled = cart.items.length === 0;

    const count = getCount();
    document.querySelectorAll(".nav-item__badge").forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
        badge.setAttribute("aria-hidden", "true");
    });
    document.querySelectorAll(".nav-item__link--button").forEach((btn) => {
        btn.setAttribute(
            "aria-label",
            count === 0 ? "Cart, empty" : `Cart, ${count} item${count === 1 ? "" : "s"}`,
        );
    });
}

function open() {
    render();
    if (!drawerEl.open) drawerEl.showModal();
}

function close() {
    drawerEl.close();
}

function checkout() {
    const cart = getCart();
    if (cart.items.length === 0) return;
    sessionStorage.setItem(CHECKOUT_STORAGE_KEY, buildSummaryText(cart));
    window.location.href = "./contact.html?checkout=1";
}

export function mountCartDrawer() {
    if (drawerEl) {
        render();
        return;
    }

    // Native <dialog>: Esc to close, focus moved in and trapped while
    // open, and restored to the Cart button on close — all for free.
    drawerEl = document.createElement("dialog");
    drawerEl.className = "cart-drawer";
    drawerEl.setAttribute("aria-labelledby", "cart-drawer-title");

    // Clicks on the backdrop land on the <dialog> element itself.
    drawerEl.addEventListener("click", (event) => {
        if (event.target === drawerEl) close();
    });

    const header = document.createElement("div");
    header.className = "cart-drawer__header";
    const heading = document.createElement("h2");
    heading.id = "cart-drawer-title";
    heading.textContent = "Your cart";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "icon-btn cart-drawer__close";
    closeBtn.setAttribute("aria-label", "Close cart");
    closeBtn.textContent = "×";
    closeBtn.addEventListener("click", close);
    header.append(heading, closeBtn);

    const body = document.createElement("div");
    body.className = "cart-drawer__body";

    const footer = document.createElement("div");
    footer.className = "cart-drawer__footer";
    const undoStatus = document.createElement("div");
    undoStatus.className = "cart-drawer__undo";
    undoStatus.setAttribute("aria-live", "polite");
    const subtotal = document.createElement("div");
    subtotal.className = "cart-drawer__subtotal";
    subtotal.setAttribute("aria-live", "polite");
    const note = document.createElement("p");
    note.className = "cart-drawer__note";
    note.textContent =
        "No payment yet. You’ll book a free call, and we confirm parts and the final price with you there.";
    const checkoutBtn = document.createElement("button");
    checkoutBtn.type = "button";
    checkoutBtn.className = "btn cart-drawer__checkout";
    checkoutBtn.textContent = "Book a call to order";
    checkoutBtn.addEventListener("click", checkout);
    footer.append(undoStatus, subtotal, note, checkoutBtn);

    drawerEl.append(header, body, footer);
    document.body.appendChild(drawerEl);

    window.addEventListener("cart:open", open);
    window.addEventListener("cart:updated", render);

    render();
}
